import { FC, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  IconButton,
  Typography,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useSnackbar } from 'notistack';
import { useForm, SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { isEqual } from 'lodash';
import { projectColors } from 'data/static';
import { AddProjectSchema } from 'data/validation/ProjectSchemas';
import { IProjectTemplate, projectTemplates } from 'data/templates/projectTemplates';
import { errorMessages, successMessages } from 'data/messages/messages';
import { TRtkErrorType } from 'data/types/general.types';
import Button from 'components/Base/Button';
import TemplateSelection from '../TemplateSelection';
import AddEditNewProjectForm, {
  TAddEditNewProjectForm,
} from '../AddEditNewProjectModal/AddEditNewProjectForm';

type TCreateProjectModalProps = {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: TAddEditNewProjectForm, template: IProjectTemplate | null) => Promise<void>;
  isLoading: boolean;
};

const steps = ['Choose template', 'Project details'];

const CreateProjectModal: FC<TCreateProjectModalProps> = ({
  open,
  onClose,
  onSubmit,
  isLoading,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [activeStep, setActiveStep] = useState(0);
  const [selectedTemplate, setSelectedTemplate] = useState<IProjectTemplate | null>(null);

  const { handleSubmit, formState, register, reset, setValue, watch } =
    useForm<TAddEditNewProjectForm>({
      defaultValues: {
        name: '',
        color: projectColors[0].light,
        description: '',
      },
      resolver: yupResolver(AddProjectSchema),
    });

  const handleClose = () => {
    setActiveStep(0);
    setSelectedTemplate(null);
    reset();
    onClose();
  };

  const handleTemplateSelect = (template: IProjectTemplate) => {
    setSelectedTemplate(template);

    // Pre-fill form with template data
    if (template.suggestedName) {
      setValue('name', template.suggestedName);
    }

    // Find matching color or use first
    const matchingColor = projectColors.find(c => c.dark === template.color);
    if (matchingColor) {
      setValue('color', matchingColor.light);
    }

    setActiveStep(1);
  };

  const handleBack = () => {
    setActiveStep(0);
    setSelectedTemplate(null);
  };

  const onFormSubmit: SubmitHandler<TAddEditNewProjectForm> = async (values) => {
    try {
      console.log('=== CreateProjectModal onFormSubmit ===');
      console.log('Selected template:', selectedTemplate);
      console.log('Form values:', values);

      const trimDescription = values.description?.trim();
      await onSubmit(
        {
          ...values,
          description: trimDescription || null,
        },
        selectedTemplate
      );
      enqueueSnackbar(successMessages.toast, { variant: 'success' });
      handleClose();
    } catch (err) {
      const error = err as TRtkErrorType;
      enqueueSnackbar(error?.data?.message || errorMessages.default, {
        variant: 'error',
      });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {activeStep === 1 && (
            <IconButton
              onClick={handleBack}
              size="small"
              sx={{ mr: 0.5 }}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          )}
          <Typography variant="h6" component="span">
            Create a project
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <Box sx={{ px: 3, pb: 1 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      <DialogContent sx={{ pt: 2 }}>
        {activeStep === 0 && (
          <TemplateSelection onSelectTemplate={handleTemplateSelect} />
        )}

        {activeStep === 1 && (
          <Box>
            {selectedTemplate && selectedTemplate.id !== 'blank' && (
              <Box
                sx={{
                  mb: 3,
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: `${selectedTemplate.color}10`,
                  border: '1px solid',
                  borderColor: `${selectedTemplate.color}30`,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ color: 'text.secondary', fontWeight: 500 }}
                >
                  Template: <strong style={{ color: selectedTemplate.color }}>{selectedTemplate.name}</strong>
                </Typography>
                {selectedTemplate.boards.length > 0 && (
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Will create {selectedTemplate.boards.length} board(s) and {selectedTemplate.docs.length} doc(s)
                  </Typography>
                )}
              </Box>
            )}
            <AddEditNewProjectForm
              formState={formState}
              register={register}
              watch={watch}
              setValue={setValue}
              onSubmit={handleSubmit(onFormSubmit)}
            />
          </Box>
        )}
      </DialogContent>

      {activeStep === 1 && (
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            variant="text"
            onClick={handleClose}
            sx={{ color: 'text.secondary' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit(onFormSubmit)}
            loading={isLoading}
            disabled={formState.isSubmitting || isLoading}
          >
            Create project
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default CreateProjectModal;
